import { TableCard, TableOutput } from "@general-backoffice/core";
import type { PaginationMeta, Referral } from "@investhinks/api-client";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, Card } from "reactstrap";

import { TypedTable, type TypedTableColumn } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";

interface PartnerReferralFilters extends Record<string, unknown> {
  expired?: boolean | null;
  claimed?: boolean;
  pending?: boolean;
  search?: string;
}

interface PartnerReferralsTableCardProps {
  referrals: Referral[];
  totalReferrals: number;
  isLoading: boolean;
  pagination?: PaginationMeta;
  goToPage?: (page: number) => void;
  filters?: PartnerReferralFilters;
  resetFilters?: () => void;
  resetPagination?: () => void;
  setFilters?: (
    filters: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)
  ) => void;
  changeFilters?: (field: string, value: unknown) => void;
  onUserClick?: ((referral: Referral) => void) | null;
  title?: string;
  hideButtons?: boolean;
  columnsToHide?: string[];
  [key: string]: unknown;
}

const PartnerReferralsTableCard = ({
  referrals,
  totalReferrals,
  isLoading,
  pagination = { page: 1, pageSize: 25, pageCount: 1, total: 0 },
  goToPage = () => {},
  filters = {},
  resetFilters = () => {},
  resetPagination = () => {},
  setFilters = () => {},
  changeFilters = () => {},
  onUserClick,
  title,
  hideButtons,
  columnsToHide = [],
}: PartnerReferralsTableCardProps) => {
  const { t } = useTranslation("referral", { keyPrefix: "partnerReferralsTableCard" });
  // The default used to be the literal string "title", which shipped to users.
  const cardTitle = title ?? t("title");

  const hideColumns = (field: string, column: TypedTableColumn<Referral>): TypedTableColumn<Referral>[] =>
    columnsToHide.includes(field) ? [] : [column];

  const columns: TypedTableColumn<Referral>[] = [
    {
      name: t("user"),
      compressed: true,
      formattedContent: ({ referee }) => (
        <>
          <div>{referee?.name}</div>
          <small className="font-weight-bold">{referee?.email}</small>
        </>
      ),
      isTh: true,
    },
    ...hideColumns("badges", {
      name: "",
      formattedContent: ({ referee }) => {
        const bookmarks = [];
        if (!referee?.isActive)
          bookmarks.push({
            icon: <i className="fas fa-exclamation-triangle" />,
          });
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    }),
    {
      name: t("accessMethod"),
      formattedContent: ({ referral_code }) => referral_code?.name || t("linkAccessMethod"),
    },
    ...hideColumns("uncollectedCommission", {
      name: t("commissionToClaim"),
      isTh: true,
      formattedContent: ({ uncollectedCommission }) => (uncollectedCommission || 0).toFixed(2) + " €",
    }),
    ...hideColumns("totalCommission", {
      name: t("totalCommission"),
      formattedContent: ({ totalCommission }) => (totalCommission || 0).toFixed(2) + " €",
    }),
    {
      name: t("registerDate"),
      formattedContent: ({ createdAt }) => moment(createdAt).format("LL"),
    },
  ];

  const buttons = [
    {
      children: t("activeFilter"),
      active: !filters?.pending && !filters?.expired && !filters?.claimed,
      onClick: () => resetFilters(),
    },
    {
      children: t("pendingFilter"),
      active: filters?.pending && !filters?.claimed,
      onClick: () =>
        setFilters({
          pending: true,
          expired: null,
          claimed: false,
        }),
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header
        title={title ? t(title, { defaultValue: title }) : cardTitle}
        subtitle={totalReferrals ? t("subtitle", { numReferrals: totalReferrals }) : undefined}
        search={{
          value: filters?.["search"],
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => changeFilters("search", e.target.value),
          msToChange: 1000,
          placeholder: "Search...",
        }}
        isLoading={isLoading}
      >
        <ButtonGroup className="mr-3">
          {!hideButtons &&
            buttons.map((b, index) => (
              <Button
                {...b}
                size={"sm"}
                onClick={() => {
                  resetPagination();
                  b.onClick();
                }}
                key={index}
                color="default"
                outline
              />
            ))}
        </ButtonGroup>
      </TableCard.Header>
      <TypedTable
        columns={columns}
        rows={referrals}
        isLoading={isLoading}
        noContentMessage={t("emptyReferrals")}
        rowAmount={pagination.pageSize}
        onRowClick={onUserClick ? (r) => onUserClick(r) : undefined}
        selectable={false}
        sortable={false}
      />
      <PaginatedTableFooter
        {...{
          isLoading,
          pagination,
          goToPage,
        }}
      />
    </Card>
  );
};

export default PartnerReferralsTableCard;
